import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { Router } from '@angular/router';
import { LoadingController, ModalController, ToastController } from '@ionic/angular';
import * as moment from 'moment';
import {
    ConfirmCloseStepModalComponent
} from "src/app/performance-cycles/cycle/confirm-close-step-modal/confirm-close-step-modal.component";
import { BaseClass } from 'src/app/shared/classes/base';
import { FiltersModal } from 'src/app/shared/modals/filters/filters.modal';
import {
    AttributeFilter,
    AttributeTypeEnum,
    FilterItem,
    FilterModel
} from 'src/app/shared/modals/filters/models/filter-models';
import { ProcessUserListModal } from 'src/app/shared/modals/process-user-list/process-user-list.modal';
import { AuthService } from 'src/app/shared/services/auth.service';
import { TranslationService } from 'src/app/shared/services/translation.service';
import { PerformanceCycleService } from '../performance-cycles.service';
import { CyclePreview, CycleProcess } from 'src/app/shared/interfaces/cycle.interface';

@Component({
    selector: 'app-cycle',
    templateUrl: './cycle.component.html',
    styleUrls: ['./cycle.component.scss']
})
export class CycleComponent extends BaseClass implements OnInit {

    @Input() performanceCycle: CyclePreview;
    @Input() readonly cycleConfigurationAvailable: boolean = false;
    @Input() readonly isLoadingSteps: boolean = false;

    @Output() selectYear = new EventEmitter();

    public canManageSteps = false;

    public filters: FilterModel[];
    public selectedFilters: FilterItem[] = [];

    constructor(
            protected _toastController: ToastController,
            protected _translationService: TranslationService,
            private _modalController: ModalController,
            private _router: Router,
            private _loadingController: LoadingController,
            private _performanceCycleService: PerformanceCycleService,
            private _authService: AuthService,
    ) {
        super(_toastController, _translationService);
    }

    ngOnInit(): void {
        this.filters = [
            new FilterModel('filters.subordinates', [
                new FilterItem('directSubordinates', 'filters.directSubordinates', 'true', false)
            ], false, false),
            new FilterModel('filters.userStatus', [
                new FilterItem('userStatus', 'filters.activeUsers', '1', false),
                new FilterItem('userStatus', 'filters.blockedUsers', '2', false)
            ], false, false)
        ];

        this.filters.push(...AttributeTypeEnum.values().map(x =>
                new AttributeFilter(x, [], true)
        ));

        this.canManageSteps = this._authService.isInRole('Impersonate') || this._authService.isInRole('HR');
    }

    public async viewUsers(process: CycleProcess) {
        process.cycleId = this.performanceCycle.id;

        const attributeIds = this.getSelectedAttributeIds();
        const directSubordinates = this.directSubordinatesSelected();
        const userStatus = this.getUserStatusFilterValue();

        return (
                await this._modalController.create({
                    component: ProcessUserListModal,
                    cssClass: 'process-user-list',
                    componentProps: {
                        'process': process,
                        'attributeIds': attributeIds,
                        'directSubordinates': directSubordinates,
                        'userStatus': userStatus,
                        'cycle': this.performanceCycle
                    }
                })
        ).present();
    }

    public getSelectedFilters(): FilterItem[] {
        const filters: FilterItem[] = [];
        for (const filter of this.filters) {
            filters.push(...filter.items.filter(i => i.selected));
        }
        return filters;
    }

    public getSelectedAttributeIds(): number[] {
        return this.selectedFilters.filter(x => x.id === 'attributeIds').map(x => parseInt(x.value, 10));
    }

    public directSubordinatesSelected(): boolean {
        const subordinatesFilter = this.selectedFilters.filter(x => x.id === 'directSubordinates');
        return subordinatesFilter && subordinatesFilter.length > 0
                ? subordinatesFilter[0].value === 'true'
                :false;
    }

    public getUserStatusFilterValue(): number {
        const activeUsersFilter = this.selectedFilters.filter(x => x.id === 'userStatus');
        return activeUsersFilter && activeUsersFilter.length > 0
                ? activeUsersFilter[0].value ? parseInt(activeUsersFilter[0].value, 10):null
                :null;
    }

    public async openFilters() {
        const modal = await this._modalController.create({
            component: FiltersModal,
            cssClass: 'bigger-modal',
            componentProps: {
                'filters': this.filters
            }
        });
        modal.onDidDismiss().then(result => {
            this.selectedFilters = this.getSelectedFilters();
            this.applyFilters();
        });
        modal.present();
    }

    public removeFilter(index: number) {
        this.selectedFilters[index].selected = false;
        this.selectedFilters.splice(index, 1);
        this.applyFilters();
    }

    private async applyFilters(): Promise<void> {
        const loading = await this._loadingController.create();
        await loading.present();

        const year = moment().year();

        const attributeIds = this.getSelectedAttributeIds();
        const directSubordinates = this.directSubordinatesSelected();
        const userStatus = this.getUserStatusFilterValue();

        this._performanceCycleService.getPeformanceCycles(
                year, this.performanceCycle.id, directSubordinates, userStatus, attributeIds
        ).subscribe((response) => {
            this.performanceCycle = response.data[0];
            loading.dismiss();
        }, (error) => {
            loading.dismiss();
            this.getErrorNotification(error);
        });
    }


    // private _closeProcess(user: CycleUser): void {
    //   this._performanceCycleService.closeProcessForUser(
    //     this.process.type, user.cycleParticipantId
    //   ).subscribe(() => {
    //     this.notify(
    //       this._translationService.getTranslation(
    //         'performanceCycles.processClosed'
    //       ), 'success'
    //     );
    //     user.status = ProcessStatusEnum.AllowedViewing;
    //   }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    // }

    public enableStep(process: CycleProcess) {
        if (confirm(this._translationService.getTranslation('performanceCycles.confirmOpenStep'))) {
            this._performanceCycleService.enableStepForAllUsers(process.type, this.performanceCycle.id, process.cycleProcessConfigId, this.performanceCycle.closed).subscribe(() => {
                this.notify(
                        this._translationService.getTranslation(
                                'performanceCycles.stepEnabled'
                        ), 'success'
                ).then(() => {
                    setTimeout(() => {
                        this.selectYear.emit({ cycleId: this.performanceCycle.id })
                    }, 1000);
                });
            }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
        }
    }

    public async openConfirmCloseStepModal(process: CycleProcess) {
        const modal = await this._modalController.create({
            component: ConfirmCloseStepModalComponent,
            cssClass: 'confirm-modal',
        });
        modal.onDidDismiss().then((result) => {
            if (result.data) {
                this._closeStep(process);
            }
        });
        await modal.present();
    }

    private _closeStep(process: CycleProcess) {
        this._performanceCycleService.closeProcessForAllUsers(process.type, this.performanceCycle.id, process.cycleProcessConfigId, this.performanceCycle.closed).subscribe(() => {
            this.notify(
                    this._translationService.getTranslation(
                            'performanceCycles.processClosed'
                    ), 'success'
            ).then(() => {
                setTimeout(() => {
                    this.selectYear.emit({ cycleId: this.performanceCycle.id })
                }, 1000);
            });
        }, (error) => this.notify(this.getErrorNotification(error), 'danger'));
    }


}
